import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { fulfillmentApi } from '../../api/fulfillment-api';
import { ConflictError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import type { FulfillmentOrder } from '../../domain/fulfillment';
import { fulfillmentErrorMessage, isUnknownOutcome } from './error-message';
import { RequirementsEditor } from './RequirementsEditor';
import { newRequirementRow, requirementRowsError, type RequirementRow } from './requirement-rows';
import type { FulfillmentReferences } from './use-fulfillment-references';

/** Replaces a DRAFT order's lines, checked against the loaded version. */
export function EditLinesDrawer({
  order,
  references,
  onClose,
  onSaved,
  onReload,
}: {
  order: FulfillmentOrder;
  references: FulfillmentReferences;
  onClose: () => void;
  onSaved: (order: FulfillmentOrder) => void;
  onReload: () => void;
}) {
  const [rows, setRows] = useState<RequirementRow[]>(() =>
    order.lines.map((line) =>
      newRequirementRow(line.requirement.type, line.requirement, line.locationId),
    ),
  );
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [unknown, setUnknown] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      fulfillmentApi.updateOrder({
        id: order.id,
        version: order.version,
        lines: rows.map((row) => ({ requirement: row.requirement, locationId: row.locationId })),
      }),
    onSuccess: onSaved,
    onError: (failure: unknown) => {
      if (isUnknownOutcome(failure)) setUnknown(true);
      else {
        setConflict(failure instanceof ConflictError);
        setError(fulfillmentErrorMessage(failure, 'The lines could not be saved.'));
      }
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setConflict(false);
    if (rows.length === 0) {
      setError('An order needs at least one line.');
      return;
    }
    const rowError = requirementRowsError(rows);
    if (rowError) {
      setError(rowError);
      return;
    }
    save.mutate();
  }

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer drawer-wide"
        aria-label="Edit order lines"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <h2>Edit lines</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close edit lines drawer"
          >
            ×
          </button>
        </div>
        {error ? (
          <div role="alert" className="form-error">
            {error}
            {conflict ? (
              <button type="button" className="link-button" onClick={onReload}>
                Reload order
              </button>
            ) : null}
          </div>
        ) : null}
        {unknown ? <PartialFailureNotice onCheckCurrentState={onReload} /> : null}
        <RequirementsEditor
          label="Lines"
          rows={rows}
          onChange={setRows}
          references={references}
          showLocations
        />
        <div className="drawer-actions">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save lines'}
          </button>
        </div>
      </form>
    </div>
  );
}
